import { R_PLANET, TAU } from '../constants';
import { angleDist, inArc } from '../polar';
import { randRange } from '../rng';
import { acquireEffect, pushEvent } from '../world';
import type { Content, Enemy, World } from '../types';
import { spawnChildAt } from './spawning';
import { spawnDrop } from './drops';

/** Frozen targets are brittle — this is what makes Deep Freeze a damage ult. */
export const FROZEN_DAMAGE_MULT = 2.5;
/** A thaw below this HP fraction shatters the target outright. */
export const SHATTER_THRESHOLD = 0.35;

/**
 * `fromTheta` is the bearing the damage arrives from. Cruisers hold a shield
 * facing the planet, so anything hitting them from a flanking arc bypasses it
 * — that's the whole counter-play for the type.
 */
export function damageEnemy(
  world: World,
  content: Content,
  e: Enemy,
  amount: number,
  fromTheta: number | null = null,
): void {
  if (!e.alive || amount <= 0) return;

  let dmg = amount;

  if (e.def.behaviour === 'shielded' && fromTheta !== null) {
    // The shield faces the planet, i.e. inward — the enemy's own bearing.
    const arc = e.def.shieldArc ?? 0.9;
    const facing = e.theta;
    if (angleDist(fromTheta, facing) <= arc) {
      dmg *= e.def.shieldMult ?? 0.25;
    }
  }

  // Armour is flat, so twenty small hits bounce where one big one lands. It's
  // the reason a Beam Lance exists alongside a Flak Battery.
  if (e.def.behaviour === 'armoured') {
    dmg = Math.max(1, dmg - (e.def.armour ?? 6));
  }

  if (e.freezeT > 0) dmg *= FROZEN_DAMAGE_MULT;

  e.hp -= dmg;
  if (e.hp <= 0) killEnemy(world, content, e, true);
}

export function killEnemy(
  world: World,
  content: Content,
  e: Enemy,
  reward: boolean,
): void {
  if (!e.alive) return;

  // Snapshot everything we need before freeing the slot. The first child of a
  // split will very often be handed this same pool object back, at which point
  // `e` describes the child, not the thing that just died.
  const def = e.def;
  const theta = e.theta;
  const r = e.r;
  const underRing = e.underRing;

  e.alive = false;
  e.phase = 'dead';

  if (reward) {
    world.stats.kills++;
    pushEvent(world, { type: 'kill', theta, r, amount: def.value, id: def.id });
    // Juice are not credited here — they drop on the field and have to be
    // picked up. The multiplier is applied at collection, not at death, so a
    // Mint bought mid-level pays out on juice already lying around.
    spawnDrop(world, theta, r, def.value);
  }

  if (def.behaviour === 'splitter' && def.splitInto) {
    const n = def.splitCount ?? 2;
    for (let i = 0; i < n; i++) {
      const spread = 0.34;
      const offset = n === 1 ? 0 : (i / (n - 1) - 0.5) * spread * 2;
      spawnChildAt(world, content, def.splitInto, theta + offset, r, underRing);
    }
  }
}

/** Applied when the freeze timer runs out — brittle targets come apart. */
export function thaw(world: World, content: Content, e: Enemy): void {
  if (e.hp <= e.maxHp * SHATTER_THRESHOLD) {
    const fx = acquireEffect(world, 'shatter');
    if (fx) {
      fx.theta = e.theta;
      fx.r = e.r;
      fx.duration = 0.4;
      fx.spread = 0;
      fx.front = e.def.size;
    }
    killEnemy(world, content, e, true);
  }
}

/** Splash damage around a point. Polar, so it's two comparisons per enemy. */
export function splashDamage(
  world: World,
  content: Content,
  theta: number,
  r: number,
  radius: number,
  amount: number,
): void {
  if (radius <= 0) return;
  // Convert the linear splash radius into an angular tolerance at this range.
  const angular = radius / Math.max(r, 1);
  for (const e of world.enemies) {
    if (!e.alive) continue;
    if (Math.abs(e.r - r) > radius) continue;
    if (angleDist(e.theta, theta) > angular) continue;
    damageEnemy(world, content, e, amount, theta);
  }
}

/** Damage to everything inside an arc and radius band. Used by ultimates. */
export function arcDamage(
  world: World,
  content: Content,
  theta: number,
  spread: number,
  rMin: number,
  rMax: number,
  amount: number,
  skip?: Set<number>,
): number {
  let hits = 0;
  for (const e of world.enemies) {
    if (!e.alive) continue;
    if (skip?.has(e.id)) continue;
    if (e.r < rMin || e.r > rMax) continue;
    if (spread < TAU && !inArc(e.theta, theta, spread)) continue;
    skip?.add(e.id);
    damageEnemy(world, content, e, amount, theta);
    hits++;
  }
  return hits;
}

/** Enemy reaches the crust. Costs Core Integrity; the enemy is spent. */
export function coreImpact(world: World, content: Content, e: Enemy): void {
  let dmg = e.def.damage;

  // Shield Pylons soak core damage.
  const shield = coreShieldMult(world, content);
  dmg *= shield;

  world.coreHp -= dmg;
  world.coreDamageTaken += dmg;
  pushEvent(world, { type: 'core-hit', theta: e.theta, r: R_PLANET, amount: dmg });

  const fx = acquireEffect(world, 'core-hit');
  if (fx) {
    fx.theta = e.theta;
    fx.r = R_PLANET;
    fx.duration = 0.5;
    fx.spread = randRange(world.rng, 0.3, 0.5);
    fx.front = 0;
  }

  killEnemy(world, content, e, false);

  if (world.coreHp <= 0) {
    world.coreHp = 0;
    world.waveState = 'lost';
    pushEvent(world, { type: 'level-lost' });
  }
}

export function coreShieldMult(world: World, content: Content): number {
  let mult = 1;
  for (const slot of world.slots) {
    const st = slot.structure;
    if (!st) continue;
    const def = content.structures.get(st.defId);
    if (def?.support !== 'core-shield') continue;
    const tier = def.tiers[st.tier];
    mult *= tier?.auraMult ?? 0.9;
  }
  return mult;
}
